import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs
} from '@chakra-ui/react'
import ConfirmDiscardDialog from '../ConfirmDiscardDialog'
import IssueCreditForm from './IssueCreditForm'
import IssueRecurringCreditForm from './IssueRecurringCreditForm'

type InitialCreditTab = 'oneTime' | 'recurring'

const ALL_CREDIT_TABS: InitialCreditTab[] = ['oneTime', 'recurring']
const CREDIT_TAB_LABELS: Record<InitialCreditTab, string> = {
  oneTime: 'One-time Credit',
  recurring: 'Recurring Credit'
}

const IssueCreditModal: React.FC<
  React.PropsWithChildren<{
    availableTabs?: InitialCreditTab[]
    close: () => void
    initialTab?: InitialCreditTab
    isOpen: boolean
    onSuccess?: (createdCreditIds?: string[]) => void | Promise<void>
    rentalId: string
  }>
> = ({
  availableTabs = ALL_CREDIT_TABS,
  close,
  initialTab = 'oneTime',
  isOpen,
  onSuccess,
  rentalId
}) => {
  const creditTabs = availableTabs.length > 0 ? availableTabs : ALL_CREDIT_TABS
  const initialTabIndex = Math.max(creditTabs.indexOf(initialTab), 0)
  const [tabIndex, setTabIndex] = useState(initialTabIndex)
  const [isOneTimeCreditDirty, setIsOneTimeCreditDirty] = useState(false)
  const [isRecurringCreditDirty, setIsRecurringCreditDirty] = useState(false)
  const discardCancelRef = useRef<HTMLButtonElement | null>(null)
  const [isDiscardConfirmationOpen, setIsDiscardConfirmationOpen] = useState(false)
  const hasUnsavedChanges =
    (creditTabs.includes('oneTime') && isOneTimeCreditDirty) ||
    (creditTabs.includes('recurring') && isRecurringCreditDirty)

  useEffect(() => {
    if (isOpen) {
      setTabIndex(initialTabIndex)
    }
  }, [initialTabIndex, isOpen])

  const requestClose = () => {
    if (hasUnsavedChanges) {
      setIsDiscardConfirmationOpen(true)
      return
    }

    close()
  }

  const discardAndClose = () => {
    setIsDiscardConfirmationOpen(false)
    setIsOneTimeCreditDirty(false)
    setIsRecurringCreditDirty(false)
    close()
  }

  const submitAndClose = (createdCreditIds?: string[]) => {
    setIsOneTimeCreditDirty(false)
    setIsRecurringCreditDirty(false)
    close()

    Promise.resolve(onSuccess?.(createdCreditIds)).catch((error) => {
      console.error('Unable to refresh ledger after issuing credit', error)
    })
  }

  return (
    <>
      <Modal size='5xl' isOpen={isOpen} onClose={requestClose} blockScrollOnMount={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Issue Credit</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Tabs
              colorScheme='teal'
              index={tabIndex}
              isFitted
              isLazy
              lazyBehavior='keepMounted'
              onChange={setTabIndex}
            >
              <TabList mb={4}>
                {creditTabs.map((creditTab) => (
                  <Tab key={creditTab}>{CREDIT_TAB_LABELS[creditTab]}</Tab>
                ))}
              </TabList>
              <TabPanels>
                {creditTabs.map((creditTab) => (
                  <TabPanel key={creditTab} px={0}>
                    {creditTab === 'oneTime' ? (
                      <IssueCreditForm
                        onDirtyChange={setIsOneTimeCreditDirty}
                        onSubmit={submitAndClose}
                        rentalId={rentalId}
                      />
                    ) : (
                      <IssueRecurringCreditForm
                        onDirtyChange={setIsRecurringCreditDirty}
                        onSubmit={submitAndClose}
                        rentalId={rentalId}
                      />
                    )}
                  </TabPanel>
                ))}
              </TabPanels>
            </Tabs>
          </ModalBody>
        </ModalContent>
      </Modal>
      <ConfirmDiscardDialog
        cancelRef={discardCancelRef}
        isOpen={isDiscardConfirmationOpen}
        onClose={() => setIsDiscardConfirmationOpen(false)}
        onDiscard={discardAndClose}
        resourceLabel='credit'
      />
    </>
  )
}

IssueCreditModal.propTypes = {
  availableTabs: PropTypes.arrayOf(
    PropTypes.oneOf<InitialCreditTab>(['oneTime', 'recurring']).isRequired
  ),
  close: PropTypes.func.isRequired,
  initialTab: PropTypes.oneOf(['oneTime', 'recurring']),
  isOpen: PropTypes.bool.isRequired,
  onSuccess: PropTypes.func,
  rentalId: PropTypes.string.isRequired
}

export default IssueCreditModal
